import TextInput from '~/components/TextInput';
import DateInput from '~/components/DateInput';
import TextArea from '~/components/TextArea';
import ClientPicker from '~/components/ClientPicker';
import type { QuestionSettings } from '~/lib/Questions/Questions.types';
import { useInvoiceContext } from '~/lib/InvoiceContext';
import ErrorMessageBlock from '~/components/ErrorMessageBlock';

interface EditBillingInfoFormProps {
    settings: QuestionSettings;
    setSettings: (settings: QuestionSettings) => void;
}

const EditBillingInfoForm = ({ settings, setSettings }: EditBillingInfoFormProps) => {
    const invoiceContext = useInvoiceContext();
    const client = invoiceContext?.client;
    const setClient = invoiceContext?.setClient;
    const updateInvoice = invoiceContext?.updateInvoice;
    const invoice = invoiceContext?.invoice;

    // Use invoice_number from invoice model if available, otherwise fall back to settings
    const invoiceNumber = invoice?.invoice_number || settings?.invoiceNumber || '';
    const issueDate = settings?.issueDate || '';
    // Use due_at from invoice model if available, otherwise fall back to settings
    const dueDate = invoice?.due_at || settings?.dueDate || '';
    const notes = settings?.notes || '';

    const updateSetting = (field: string, value: any) => {
        setSettings({ ...settings, [field]: value });

        // If updating due date, also update the invoice model
        if (field === 'dueDate' && updateInvoice) {
            updateInvoice({ due_at: value });
        }
    };

    return (
        <div className="space-y-8">
            <div>
                {!client?.email && (
                    <ErrorMessageBlock className="mb-4">Please select which client this invoice is for.</ErrorMessageBlock>
                )}
                <div className="input-label mb-2">Client</div>
                <ClientPicker
                    value={client || { email: '', firstName: '', lastName: '', phone: '' }}
                    setValue={setClient || (() => {})}
                />
            </div>

            <div>
                <div className="input-label mb-2">Issue Date</div>
                <DateInput
                    value={issueDate}
                    onChange={(value) => updateSetting('issueDate', value)}
                    placeholder="Leave empty to use creation date"
                />
                <div className="text-xs text-gray-500 mt-1 px-3">
                    Leave empty to use the date the invoice was created
                </div>
            </div>

            <div>
                <div className="input-label mb-2">Due Date</div>
                <DateInput
                    value={dueDate}
                    onChange={(value) => updateSetting('dueDate', value)}
                    placeholder="Leave empty for default due date"
                />
                <div className="text-xs text-gray-500 mt-1 px-3">
                    Leave empty for default due date
                </div>
            </div>

            <div>
                <div className="input-label mb-2">Notes</div>
                <TextArea
                    value={notes}
                    onChange={(value) => updateSetting('notes', value)}
                    placeholder="Optional notes to display on the invoice"
                    rows={4}
                />
                <div className="text-xs text-gray-500 mt-1 px-3">
                    These notes will be displayed below the client information
                </div>
            </div>
        </div>
    );
};

export default EditBillingInfoForm;

